import { useState } from "preact/hooks";
import { authPost } from "../../api/client.ts";
import type { AuthStep } from "../../api/types.gen.ts";
import { SLOT, t } from "../../i18n/index.ts";
import { location } from "../../lib/router.ts";
import { Button } from "../../ui/Button.tsx";
import { Field, Form, Input } from "../../ui/form.tsx";
import { Slotted } from "../../ui/misc.tsx";
import { inlineButtonClass } from "../../ui/styles.ts";
import { authPath, fieldError, next, returnTo, signInElsewhere, useSubmit } from "./flow.ts";
import { AuthFrame, AuthLink, FormError } from "./frame.tsx";

/** Signing in: email and password, or a code by email; single sign-on on the identity provider's pages. */
export function SignIn() {
  const [email, setEmail] = useState(location.value.searchParams.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [emailNeeded, setEmailNeeded] = useState(false);
  const signIn = useSubmit(async () => next(await authPost<AuthStep>("/sign-in", { email, password })));
  const code = useSubmit(async () => next(await authPost<AuthStep>("/magic", { email })));
  const error = code.error ?? signIn.error;
  return (
    <AuthFrame
      title={t("auth.signIn.title")}
      footer={
        <Slotted text={t("auth.signIn.new", { link: SLOT })}>
          <AuthLink href={authPath("/signup")}>{t("auth.signIn.create")}</AuthLink>
        </Slotted>
      }
    >
      <FormError error={error} email={email} />
      <Form class="flex flex-col gap-4" onSubmit={signIn.submit}>
        <Field
          label={t("auth.email")}
          error={(emailNeeded && !email.trim() ? t("auth.badEmail") : null) ?? fieldError(error, "email")}
        >
          <Input type="email" autocomplete="username" required={true} value={email} onValue={setEmail} />
        </Field>
        <Field label={t("auth.password")}>
          <Input
            type="password"
            autocomplete="current-password"
            required={true}
            value={password}
            onValue={setPassword}
          />
        </Field>
        <div class="-mt-2 flex justify-end">
          <AuthLink href={authPath("/forgot-password", email.trim() ? { email: email.trim() } : {})}>
            {t("auth.signIn.forgot")}
          </AuthLink>
        </div>
        <Button variant="primary" submit={true} busy={signIn.busy} class="w-full">
          {t("auth.signIn.submit")}
        </Button>
      </Form>
      <p class="my-5 flex items-center gap-3 text-xs text-zinc-500 uppercase before:h-px before:flex-1 before:bg-rule after:h-px after:flex-1 after:bg-rule">
        {t("auth.or")}
      </p>
      <div class="flex flex-col items-center gap-3">
        <Button
          class="w-full"
          busy={code.busy}
          onClick={() => {
            setEmailNeeded(true);
            if (email.trim()) {
              code.submit();
            }
          }}
        >
          {t("auth.signIn.code")}
        </Button>
        <button type="button" class={inlineButtonClass()} onClick={() => signInElsewhere(returnTo(), email.trim())}>
          {t("auth.signIn.sso")}
        </button>
      </div>
    </AuthFrame>
  );
}
